/**
 * Error Report Queue Service
 * Stocke les rapports d'erreur en IndexedDB et gère le retry automatique
 * 
 * @module errorReportQueue
 */

import { offlineDb, type LocalErrorReportQueue } from './offline/offlineDatabase';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/logger';

export interface ErrorReportData {
  errorType: string;
  errorMessage: string;
  stack?: string;
  componentStack?: string;
  route: string;
  timestamp: string;
  userAgent: string;
  userId?: string;
  userEmail?: string;
  screenSize?: string;
  language?: string;
  metadata?: Record<string, unknown>;
}

export interface QueueResult {
  success: boolean;
  queued: boolean;
  willRetry: boolean;
  error?: string;
}

// Configuration du retry
const MAX_RETRIES = 5;
const BASE_DELAY_MS = 500; // 500ms, 1s, 2s, 4s, 8s
const MAX_DELAY_MS = 8000;
const REPORT_TTL_DAYS = 7;
// Au-delà, une entrée restée en 'processing' est un envoi interrompu (app tuée, rechargement)
const STALE_PROCESSING_MS = 2 * 60 * 1000;
const SEND_TIMEOUT_MS = 15000;

/**
 * Génère un fingerprint unique pour éviter les doublons
 */
function generateFingerprint(report: ErrorReportData): string {
  const key = `${report.errorType}|${report.errorMessage}|${report.route}`;
  // Simple hash
  let hash = 0;
  for (let i = 0; i < key.length; i++) {
    const char = key.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return `err_${Math.abs(hash).toString(36)}`;
}

/**
 * Calcule le délai de retry avec exponential backoff
 */
function calculateRetryDelay(retryCount: number): number {
  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
  // Ajouter un peu de jitter (±20%)
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.floor(delay + jitter);
}

/**
 * Issue d'un envoi :
 *  - 'sent' : rapport reçu et enregistré ;
 *  - 'retry' : réseau, délai dépassé ou réponse non 2xx, à retenter ;
 *  - 'rejected' : reçu (l'email ou le push d'alerte a pu partir) mais non
 *    enregistré en base. Terminal : chaque nouvel essai renverrait l'alerte.
 */
type SendOutcome = 'sent' | 'retry' | 'rejected';

const REJECTED_MESSAGE = 'Reçu par send-error-report mais non enregistré en base (savedToDb:false)';

/**
 * Envoie un rapport directement à l'edge function
 */
async function sendReportDirectly(report: ErrorReportData): Promise<SendOutcome> {
  try {
    const { data, error } = await supabase.functions.invoke('send-error-report', {
      body: report,
      timeout: SEND_TIMEOUT_MS,
    });

    if (error) {
      throw error;
    }

    // La fonction répond 200 même quand l'INSERT échoue : le rapport n'est
    // pas en base, mais l'alerte est déjà partie.
    if (data && typeof data === 'object' && (data as { savedToDb?: unknown }).savedToDb === false) {
      logger.warn(`[ErrorReportQueue] ${REJECTED_MESSAGE}`);
      return 'rejected';
    }

    return 'sent';
  } catch (err) {
    logger.warn('[ErrorReportQueue] Échec envoi direct:', err);
    return 'retry';
  }
}

/** Une entrée en 'processing' plus récente que le seuil est un envoi encore en vol. */
function isFreshProcessing(item: LocalErrorReportQueue, now: number): boolean {
  return item.status === 'processing' && (item.last_attempt_at ?? 0) >= now - STALE_PROCESSING_MS;
}

/**
 * Écrit le rapport dans la file puis tente un envoi immédiat.
 *
 * L'écriture précède l'envoi : un crash, un rechargement ou une app tuée
 * pendant l'appel réseau laissait auparavant le rapport ni envoyé ni en file.
 * Si IndexedDB est indisponible, l'envoi direct est tenté quand même.
 */
export async function queueErrorReport(report: ErrorReportData): Promise<QueueResult> {
  const fingerprint = generateFingerprint(report);
  let queuedId: number | undefined;

  try {
    const now = Date.now();
    const sameReports = await offlineDb.errorReportsQueue
      .where('fingerprint')
      .equals(fingerprint)
      .and(item =>
        item.created_at > now - 24 * 60 * 60 * 1000 &&
        (item.status === 'pending' || item.status === 'processing')
      )
      .toArray();

    // Même rapport en attente, ou envoi encore en vol (dernières 24h) : inutile de le doubler.
    // Un rapport 'failed' ou 'completed' ne bloque pas une nouvelle occurrence.
    if (sameReports.some(item => item.status === 'pending' || isFreshProcessing(item, now))) {
      logger.debug('[ErrorReportQueue] Rapport dupliqué ignoré:', fingerprint);
      return { success: true, queued: false, willRetry: false };
    }

    // Restent les 'processing' figés (app tuée pendant l'envoi) : la nouvelle
    // occurrence les remplace, sinon processQueue les renverrait en double.
    for (const stale of sameReports) {
      if (stale.id === undefined) continue;
      await offlineDb.errorReportsQueue.update(stale.id, {
        status: 'failed',
        error_message: 'Envoi interrompu, remplacé par une nouvelle occurrence',
        reportData: null,
      });
    }

    // 'processing' : processQueue ne le reprend qu'une fois l'envoi jugé interrompu
    queuedId = await offlineDb.errorReportsQueue.add({
      fingerprint,
      reportData: report,
      created_at: Date.now(),
      retry_count: 0,
      status: 'processing',
      last_attempt_at: Date.now(),
    });
  } catch (err) {
    logger.warn('[ErrorReportQueue] File locale indisponible, envoi direct seul:', err);
  }

  if (navigator.onLine) {
    const outcome = await sendReportDirectly(report);

    if (outcome === 'sent') {
      if (queuedId !== undefined) {
        try {
          // Envoyé : on ne garde rien sur le téléphone. Le rapport peut porter
          // un email, des infos d'appareil et des lignes de journal.
          await offlineDb.errorReportsQueue.delete(queuedId);
        } catch {
          // Au pire, renvoyé une fois : le serveur incrémente occurrence_count.
        }
      }
      logger.info('[ErrorReportQueue] ✅ Rapport envoyé immédiatement');
      return { success: true, queued: false, willRetry: false };
    }

    if (outcome === 'rejected') {
      if (queuedId !== undefined) {
        try {
          await offlineDb.errorReportsQueue.update(queuedId, { status: 'failed', error_message: REJECTED_MESSAGE, reportData: null });
        } catch {
          // Reste en 'processing', repris après STALE_PROCESSING_MS : un renvoi au pire.
        }
      }
      // Reçu par le serveur, qui a pu alerter : pour l'écran, le rapport est parti.
      return { success: true, queued: false, willRetry: false, error: REJECTED_MESSAGE };
    }
  }

  if (queuedId === undefined) {
    return {
      success: false,
      queued: false,
      willRetry: false,
      error: 'Envoi impossible et file locale indisponible',
    };
  }

  // Échec ou offline → à reprendre par processQueue
  try {
    await offlineDb.errorReportsQueue.update(queuedId, { status: 'pending' });
  } catch {
    // Reste en 'processing' : repris après STALE_PROCESSING_MS.
  }

  logger.info('[ErrorReportQueue] 📦 Rapport mis en queue pour retry:', fingerprint);

  return { success: true, queued: true, willRetry: true };
}

/**
 * Traite tous les rapports en attente dans la queue
 */
export async function processQueue(): Promise<{ processed: number; failed: number }> {
  if (!navigator.onLine) {
    logger.debug('[ErrorReportQueue] Offline, skip processing');
    return { processed: 0, failed: 0 };
  }
  
  let processed = 0;
  let failed = 0;
  
  try {
    // Envoi interrompu (app tuée pendant l'appel) : l'entrée redevient à reprendre
    await offlineDb.errorReportsQueue
      .where('status')
      .equals('processing')
      .and(item => (item.last_attempt_at ?? 0) < Date.now() - STALE_PROCESSING_MS)
      .modify({ status: 'pending' });

    // Récupérer les rapports pending qui n'ont pas atteint le max de retries
    const pendingReports = await offlineDb.errorReportsQueue
      .where('status')
      .equals('pending')
      .and(item => item.retry_count < MAX_RETRIES)
      .toArray();
    
    // Muet quand il n'y a rien : ce passage tourne toutes les 30 s et le
    // tampon de 50 lignes joint aux rapports ne doit pas s'en remplir.
    if (pendingReports.length > 0) {
      logger.debug(`[ErrorReportQueue] ${pendingReports.length} rapport(s) à traiter`);
    }
    
    for (const report of pendingReports) {
      // Calculer le délai avant retry
      const delay = calculateRetryDelay(report.retry_count);
      const timeSinceLastAttempt = Date.now() - (report.last_attempt_at || 0);
      
      if (timeSinceLastAttempt < delay) {
        // Pas encore le moment de retry
        continue;
      }
      
      // Marquer comme processing
      await offlineDb.errorReportsQueue.update(report.id!, {
        status: 'processing',
        last_attempt_at: Date.now(),
      });
      
      // Tenter l'envoi
      const outcome = await sendReportDirectly(report.reportData);

      if (outcome === 'rejected') {
        // Terminal : un nouvel essai renverrait l'email et le push d'alerte.
        await offlineDb.errorReportsQueue.update(report.id!, {
          status: 'failed',
          error_message: REJECTED_MESSAGE,
          reportData: null,
        });
        failed++;
      } else if (outcome === 'sent') {
        // Succès → supprimé, rien ne reste sur le téléphone
        await offlineDb.errorReportsQueue.delete(report.id!);
        processed++;
        logger.info('[ErrorReportQueue] ✅ Rapport envoyé après retry:', report.fingerprint);
      } else {
        // Échec → incrémenter retry_count
        const newRetryCount = report.retry_count + 1;
        
        if (newRetryCount >= MAX_RETRIES) {
          // Max retries atteint → marquer comme failed
          await offlineDb.errorReportsQueue.update(report.id!, {
            status: 'failed',
            retry_count: newRetryCount,
            error_message: 'Max retries exceeded',
            // Abandonné : on garde la trace, pas le contenu
            reportData: null,
          });
          failed++;
          logger.warn('[ErrorReportQueue] ❌ Max retries atteint:', report.fingerprint);
        } else {
          // Remettre en pending pour retry ultérieur
          await offlineDb.errorReportsQueue.update(report.id!, {
            status: 'pending',
            retry_count: newRetryCount,
          });
        }
      }
    }
    
    // Nettoyer les anciens rapports (TTL 7 jours)
    const ttlThreshold = Date.now() - (REPORT_TTL_DAYS * 24 * 60 * 60 * 1000);
    const deleted = await offlineDb.errorReportsQueue
      .where('created_at')
      .below(ttlThreshold)
      .delete();
    
    if (deleted > 0) {
      logger.debug(`[ErrorReportQueue] 🧹 ${deleted} ancien(s) rapport(s) nettoyé(s)`);
    }
    
  } catch (err) {
    logger.error('[ErrorReportQueue] Erreur lors du processing:', err);
  }
  
  return { processed, failed };
}

/**
 * Retourne les statistiques de la queue
 */
export async function getQueueStats(): Promise<{
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}> {
  try {
    const all = await offlineDb.errorReportsQueue.toArray();
    return {
      pending: all.filter(r => r.status === 'pending').length,
      processing: all.filter(r => r.status === 'processing').length,
      completed: all.filter(r => r.status === 'completed').length,
      failed: all.filter(r => r.status === 'failed').length,
    };
  } catch {
    return { pending: 0, processing: 0, completed: 0, failed: 0 };
  }
}

/**
 * Vide la queue (pour debug/tests)
 */
export async function clearQueue(): Promise<void> {
  await offlineDb.errorReportsQueue.clear();
  logger.info('[ErrorReportQueue] Queue vidée');
}
